import { cn } from "@/lib/cn";
import { defaultNS, useTranslation } from "@/lib/i18n";
import { Card, CardContent } from "../Card/card";
import Icon from "../Icon/Icon";
import { HStack, VStack } from "../Layout";
import { Text } from "../Typography/Text";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import type { BaseProps } from "./types";

export const DropZoneCsv = ({
  getRootProps,
  getInputProps,
  isDragActive,
  acceptedFiles,
  onResetButtonClick,
  uploadCompleted,
  uploadError,
}: BaseProps) => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "component.DropZone",
  });

  return (
    <>
      <div {...getRootProps()}>
        <input {...getInputProps()} />
        <Card
          className={cn(
            "w-full h-20 shadow-inner",
            isDragActive ? "bg-neutral-200" : "bg-primary-muted-fill",
          )}
        >
          {!acceptedFiles.length && (
            <CardContent className="flex self-stretch p-0 w-full h-full">
              <VStack className="gap-2 justify-center items-center w-full h-full">
                <Label>{t("csv.dropZoneTitle")}</Label>
                <Text variant="labelSmall">{t("csv.dropZoneSubTitle")}</Text>
              </VStack>
            </CardContent>
          )}
          {acceptedFiles.length > 0 && !uploadCompleted && (
            <CardContent className="flex justify-center items-center py-4 w-full h-full">
              <Icon
                iconName="LoaderCircleIcon"
                colorVariant="neutral"
                className="animate-spin"
                size={24}
              />
            </CardContent>
          )}
          {acceptedFiles.length > 0 && uploadCompleted && (
            <CardContent className="flex justify-between py-4 w-full h-full">
              <HStack className="gap-1 items-center">
                <Icon iconName="FileSpreadsheet" />
                <span>{acceptedFiles[0].name}</span>
              </HStack>
              <Button
                variant="ghost"
                className="h-12"
                onClick={onResetButtonClick}
              >
                <Icon iconName="Trash2Icon" />
              </Button>
            </CardContent>
          )}
        </Card>
      </div>

      {uploadError && (
        <Alert variant="destructive" className="mt-3">
          <Icon iconName="AlertCircle" />
          <AlertTitle>{t("errorTitle")}</AlertTitle>
          <AlertDescription>{uploadError}</AlertDescription>
        </Alert>
      )}
    </>
  );
};
